import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngxs/store';
import { init, IStageFluxPublicAPI } from '@stage-flux/browser';
import { startWith } from 'rxjs';
import { map, pairwise } from 'rxjs/operators';
import { UserSession } from '../../../../../common/dto/user-session.dto';
import { AppState } from '../../app.state';
import { ENVIRONMENT, WINDOW } from './injection-tokens';

@Injectable()
export class TrackingService {
  private _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private _environment = inject(ENVIRONMENT);
  private _window = inject(WINDOW);
  private _store = inject(Store);

  private _stageFluxAPI?: IStageFluxPublicAPI;


  public init() {
    if (!this._environment.tracking.enabled || !this._isBrowser) {
      return;
    }

    // Do not wait for tracking initialization
    this._initializeAsync();
  }

  private async _initializeAsync() {
    await this._setupAsync();

    this._store.select(AppState.get)
      .pipe(
        map(_ => _.session),
        startWith(undefined),
        pairwise(),
      )
      .subscribe(async ([previousSession, session]) => {
        if (!!session?.sessionId) {
          if (!previousSession?.sessionId) {
            await this._identifyAsync(session);
          } else {
            await this._traitsAsync(session);
          }
        }
      });
  }

  private async _setupAsync() {
    if (this._environment.tracking.stageFlux) {
      this._stageFluxAPI = await init(this._environment.tracking.stageFlux.siteId, {
        window: this._window,
        scriptUrl: this._environment.tracking.stageFlux.scriptUrl,
      });
    }
  }

  private async _identifyAsync(session: UserSession) {
    if (!this._stageFluxAPI) {
      return;
    }

    await this._stageFluxAPI.identify(session.user.userId, {
      companyId: session.company?.companyId,
      memberId: session.company?.memberId,
      subscriptions: session?.company?.subscriptions?.map(_ => _.type).join(', '),
    });
  }

  private async _traitsAsync(session: UserSession) {
    if (!this._stageFluxAPI) {
      return;
    }

    await this._stageFluxAPI.traits({
      companyId: session.company?.companyId,
      memberId: session.company?.memberId,
      subscriptions: session?.company?.subscriptions?.map(_ => _.type).join(', '),
    });
  }
}
